/**
 * Objection Responses React Query Hooks
 * 
 * Provides React Query hooks for objection response operations with caching,
 * optimistic updates, and automatic refetching.
 */

import { useQuery, useMutation, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { 
  objectionResponseService, 
  type CreateObjectionResponseInput, 
  type UpdateObjectionResponseInput 
} from '../services/ObjectionResponseService';
import type { ObjectionResponse } from '../repositories/ObjectionResponseRepository';

type ObjectionCategory = ObjectionResponse['category'];

/**
 * Query keys for objection response queries
 */
export const objectionResponseKeys = {
  all: ['objectionResponses'] as const,
  lists: () => [...objectionResponseKeys.all, 'list'] as const,
  list: (filters?: any) => [...objectionResponseKeys.lists(), filters] as const,
  details: () => [...objectionResponseKeys.all, 'detail'] as const,
  detail: (id: string) => [...objectionResponseKeys.details(), id] as const,
  byCategory: (category: string) => [...objectionResponseKeys.lists(), 'category', category] as const,
  topPerforming: (companyId: string) => [...objectionResponseKeys.lists(), companyId, 'topPerforming'] as const,
  mostUsed: (companyId: string) => [...objectionResponseKeys.lists(), companyId, 'mostUsed'] as const,
};

/**
 * Hook to fetch all objection responses
 */
export function useObjectionResponses(
  companyId: string,
  options?: UseQueryOptions<ObjectionResponse[], Error>
) {
  return useQuery<ObjectionResponse[], Error>({
    queryKey: objectionResponseKeys.list({ companyId }),
    queryFn: () => objectionResponseService.getResponses(companyId),
    staleTime: 5 * 60 * 1000,
    enabled: !!companyId,
    ...options,
  });
}

/**
 * Hook to fetch objection responses by category
 */
export function useObjectionResponsesByCategory(
  companyId: string,
  category: ObjectionCategory,
  options?: UseQueryOptions<ObjectionResponse[], Error>
) {
  return useQuery<ObjectionResponse[], Error>({
    queryKey: objectionResponseKeys.byCategory(category),
    queryFn: () => objectionResponseService.getResponsesByCategory(companyId, category),
    staleTime: 5 * 60 * 1000,
    enabled: !!companyId && !!category,
    ...options,
  });
}

/**
 * Hook to fetch top performing responses
 */
export function useTopPerformingResponses(
  companyId: string,
  limit: number = 10,
  options?: UseQueryOptions<ObjectionResponse[], Error>
) {
  return useQuery<ObjectionResponse[], Error>({
    queryKey: objectionResponseKeys.topPerforming(companyId),
    queryFn: () => objectionResponseService.getTopPerforming(companyId, limit),
    staleTime: 10 * 60 * 1000,
    enabled: !!companyId,
    ...options,
  });
}

/**
 * Hook to fetch most used responses
 */
export function useMostUsedResponses(
  companyId: string,
  limit: number = 10,
  options?: UseQueryOptions<ObjectionResponse[], Error>
) {
  return useQuery<ObjectionResponse[], Error>({
    queryKey: objectionResponseKeys.mostUsed(companyId),
    queryFn: () => objectionResponseService.getMostUsed(companyId, limit),
    staleTime: 10 * 60 * 1000,
    enabled: !!companyId,
    ...options,
  });
}

/**
 * Hook to fetch a single objection response
 */
export function useObjectionResponse(
  id: string,
  options?: UseQueryOptions<ObjectionResponse | null, Error>
) {
  return useQuery<ObjectionResponse | null, Error>({
    queryKey: objectionResponseKeys.detail(id),
    queryFn: () => objectionResponseService.getResponse(id),
    staleTime: 5 * 60 * 1000,
    enabled: !!id,
    ...options,
  });
}

/**
 * Hook to create a new objection response
 */
export function useCreateObjectionResponse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateObjectionResponseInput) => 
      objectionResponseService.createResponse(input),
    onSuccess: (newResponse) => {
      queryClient.setQueryData(objectionResponseKeys.detail(newResponse.id), newResponse);
      queryClient.invalidateQueries({ queryKey: objectionResponseKeys.lists() });
    },
  });
}

/**
 * Hook to update an objection response
 */
export function useUpdateObjectionResponse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateObjectionResponseInput }) =>
      objectionResponseService.updateResponse(id, data),
    onMutate: async ({ id, data }) => {
      await queryClient.cancelQueries({ queryKey: objectionResponseKeys.detail(id) });
      
      const previousResponse = queryClient.getQueryData<ObjectionResponse>(
        objectionResponseKeys.detail(id)
      );
      
      if (previousResponse) {
        queryClient.setQueryData<ObjectionResponse>(objectionResponseKeys.detail(id), {
          ...previousResponse,
          ...data,
        });
      }
      
      return { previousResponse };
    },
    onError: (err, { id }, context) => {
      if (context?.previousResponse) {
        queryClient.setQueryData(objectionResponseKeys.detail(id), context.previousResponse);
      }
    },
    onSuccess: (updatedResponse, { id }) => {
      queryClient.setQueryData(objectionResponseKeys.detail(id), updatedResponse);
      queryClient.invalidateQueries({ queryKey: objectionResponseKeys.lists() });
    },
  });
}

/**
 * Hook to record usage of an objection response
 */
export function useRecordObjectionUsage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => objectionResponseService.recordUsage(id),
    onSuccess: (updatedResponse, id) => {
      queryClient.setQueryData(objectionResponseKeys.detail(id), updatedResponse);
      queryClient.invalidateQueries({ queryKey: objectionResponseKeys.mostUsed(updatedResponse.company_id) });
    },
  });
}

/**
 * Hook to update effectiveness score
 */
export function useUpdateObjectionEffectiveness() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, score }: { id: string; score: number }) => 
      objectionResponseService.updateEffectiveness(id, score),
    onSuccess: (updatedResponse, { id }) => {
      queryClient.setQueryData(objectionResponseKeys.detail(id), updatedResponse);
      queryClient.invalidateQueries({ 
        queryKey: objectionResponseKeys.topPerforming(updatedResponse.company_id) 
      });
    },
  });
}

/**
 * Hook to delete an objection response
 */
export function useDeleteObjectionResponse() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => objectionResponseService.deleteResponse(id),
    onSuccess: (_, id) => {
      queryClient.removeQueries({ queryKey: objectionResponseKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: objectionResponseKeys.lists() });
    },
  });
}
